import { styled, css } from '@vebox/etui-system';
import { memo, useMemo, useState, useEffect, createElement, Fragment } from 'react';
import { _ as _objectWithoutProperties, a as _objectSpread2, b as _extends } from '../_rollupPluginBabelHelpers-3b7bd988.js';
import { Div, Span } from '@vebox/etui-system-elements';
import '../Svg-0180d7b5.js';
import Icon from '../Icon/Icon.js';
import '@vebox/etui-icons';
import { icons } from '../Icon/icons.js';
import '../Icon/helper.js';
import '../Checkbox/CheckboxBase.js';
import '../Checkbox/CheckboxCircle.js';
import '../Checkbox/utils.js';
import Checkbox from '../Checkbox/Checkbox.js';
import { getRowKey } from './utils/get-row-key.js';
import { getIsEqual } from './utils/get-is-equal.js';

const _excluded = ["theme", "rowAs", "isRowExpanded", "isExpander"];
const TableVirtualizedInfiniteColumnDiv = styled(Div).attrs(_ref => {
  let {
      theme,
      rowAs,
      isRowExpanded,
      isExpander
    } = _ref,
    rest = _objectWithoutProperties(_ref, _excluded);
  return _objectSpread2({
    position: 'absolute',
    display: 'flex',
    height: '42px',
    alignItems: isRowExpanded ? 'flex-start' : 'center',
    bg: theme.palette.common.white,
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    textDecoration: 'none',
    p: [`${theme.spacing.xs}px `, `${theme.spacing.xs}px ${theme.spacing.sm}px`],
    as: rowAs,
    paddingLeft: `${theme.spacing[isExpander ? 'xlg' : 'xs']}px`,
    paddingTop: `${isRowExpanded ? `13px` : `${theme.spacing.xs}px`}`
  }, rest);
})``;

const ExpandedRowContainer = styled(Div)`
  position: absolute;
  z-index: 1;
  width: 100%;
  ${({
  theme
}) => css`
    background-color: ${theme.palette.grey[10]};
    padding: ${theme.spacing.xlg}px;
    border-top: 1px solid ${theme.palette.divider};
  `};
`;

const Column = /*#__PURE__*/memo(function Column({
  columnIndex,
  rowIndex,
  data,
  style
}) {
  const {
    mergedColumns,
    rawData,
    rowAs,
    rowPropsGetter,
    expandedRows,
    setExpandedRows,
    gridRef,
    expandedRowRender,
    getExpandedRowHeight,
    getRowHeight,
    tableStore,
    rowKey,
    rowSelection
  } = data;
  const column = mergedColumns[columnIndex];
  const rowDataByRowIndex = rawData[rowIndex] || null;
  const columnData = rowDataByRowIndex ? rowDataByRowIndex[column.dataIndex] : null;
  const rowProps = rowPropsGetter ? rowPropsGetter(rowDataByRowIndex) : {};
  const isExpanderCell = column.dataIndex === 'expander';
  const isCheckedCell = column.dataIndex === 'selection-column';
  const isRowExpanded = expandedRows === null || expandedRows === void 0 ? void 0 : expandedRows.includes(rowIndex);
  const rowHeight = useMemo(() => getRowHeight(rowIndex), [rowIndex]);
  const currentKey = rowDataByRowIndex ? getRowKey(rowDataByRowIndex, rowIndex, rowKey) : '';
  const handleExpanderClick = () => {
    var _gridRef$current, _gridRef$current$_lis;
    if (!rowDataByRowIndex) return;
    const updatedExpandedRows = isRowExpanded ? expandedRows.filter(index => index !== rowIndex) : [...expandedRows, rowIndex];
    setExpandedRows(updatedExpandedRows);
    gridRef === null || gridRef === void 0 ? void 0 : (_gridRef$current = gridRef.current) === null || _gridRef$current === void 0 ? void 0 : (_gridRef$current$_lis = _gridRef$current._listRef) === null || _gridRef$current$_lis === void 0 ? void 0 : _gridRef$current$_lis.resetAfterRowIndex(rowIndex);
  };
  const {
    getCheckboxProps
  } = rowSelection || {};
  const extraCheckboxProps = getCheckboxProps ? getCheckboxProps(rowDataByRowIndex) : {};
  const getIsSelected = () => {
    const {
      selectedRowKeys
    } = tableStore.getState();
    return selectedRowKeys.includes(currentKey);
  };
  const [isSelected, setIsSelected] = useState(getIsSelected());
  useEffect(() => {
    if (tableStore) {
      const setSelection = () => setIsSelected(getIsSelected());
      const uns = tableStore.subscribe(() => {
        setSelection();
      });
      setSelection();
      return () => uns;
    }
    return () => {};
  }, [tableStore, rowDataByRowIndex, currentKey]);
  const onChange = () => {
    const state = tableStore.getState();
    const {
      selectedRowKeys
    } = state;
    const nextSelection = selectedRowKeys ? isSelected ? selectedRowKeys.filter(key => key !== currentKey) : [...selectedRowKeys, currentKey] : [];
    tableStore.setState(_objectSpread2(_objectSpread2({}, state), {}, {
      selectedRowKeys: nextSelection
    }));
  };
  return /*#__PURE__*/createElement(Fragment, null, isExpanderCell && /*#__PURE__*/createElement(Div, {
    style: _objectSpread2(_objectSpread2({}, style), {}, {
      width: '100%'
    })
  }, /*#__PURE__*/createElement(TableVirtualizedInfiniteColumnDiv, {
    style: {
      zIndex: 1,
      position: 'relative',
      width: style.width,
      height: style.height
    },
    onClick: handleExpanderClick,
    as: rowAs,
    isRowExpanded: isRowExpanded,
    isExpander: true
  }, /*#__PURE__*/createElement(Span, {
    display: "flex",
    cursor: rowDataByRowIndex ? 'pointer' : 'not-allowed',
    opacity: rowDataByRowIndex ? 1 : 0.5
  }, isRowExpanded ? /*#__PURE__*/createElement(Icon, {
    size: 16,
    glyph: icons.CHEVRON_DOWN
  }) : /*#__PURE__*/createElement(Icon, {
    size: 16,
    rotate: -90,
    glyph: icons.CHEVRON_DOWN
  }))), isRowExpanded && !!expandedRowRender && /*#__PURE__*/createElement(ExpandedRowContainer, {
    height: `${getExpandedRowHeight(rowIndex, rowDataByRowIndex) - rowHeight}px`,
    top: `${rowHeight}px`
  }, expandedRowRender(rowDataByRowIndex))), !isExpanderCell && /*#__PURE__*/createElement(TableVirtualizedInfiniteColumnDiv, _extends({
    style: style,
    as: rowAs,
    isRowExpanded: isRowExpanded
  }, rowProps), isCheckedCell ? /*#__PURE__*/createElement(Checkbox, _extends({
    checked: isSelected,
    onChange: onChange
  }, extraCheckboxProps)) : column.render ? column.render(columnData, rowDataByRowIndex, columnIndex) : columnData));
}, getIsEqual);

export { Column };
